import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CoreActions } from '@next/core-lib';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { SettingsStorage } from '@next/core-lib/settings';
import { SiteActions } from '@next/core-lib/site';
import { UserSelectors } from '@next/core-lib/user';
// @ts-ignore Ignore unused commented imports
import { WindowService, WindowServiceInjector } from '@next/core-lib/window';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { combineLatest, EMPTY, Observable, of } from 'rxjs';
import {
  catchError,
  concatMap,
  exhaustMap,
  finalize,
  map,
  mergeMap,
  skipWhile,
  startWith,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { SettingsServiceHelper } from 'src/app/shared/services/settingsHelperService';

import * as feedAdviceActions from './feed-advice.actions';
import * as feedAdviceSelectors from './feed-advice.selectors';
import * as productActions from '../../product/store/actions/product.actions';
import * as settingActions from '../../settings/store/actions/settings.actions';
import * as settingSelectors from '../../settings/store/selectors/settings.selectors';
import { PigContextKeys } from '../../shared/models/shared.model';
import { FeedAdvice, FeedAdviceModuleState, FeedAdviceType } from '../models/feed-advice.model';
import { UpsertFeedAdviceResponse } from '../services/feed-advice-service.model';
import { FeedAdviceService } from '../services/feed-advice.service';
// @ts-ignore Ignore unused commented ActionTypes import

@Injectable()
export class FeedAdviceEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly router: Router,
    private readonly store: Store<FeedAdviceModuleState>,
    private readonly localeService: LocaleService,
    private readonly feedAdviceService: FeedAdviceService,
    private readonly dialogService: DialogService,
    private readonly settingsService: SettingsServiceHelper,
    @Inject(WindowServiceInjector) private readonly window: WindowService,
  ) {}

  private getPostSaveTarget(response: UpsertFeedAdviceResponse, isReferenceFeedAdvice: boolean) {
    const currentPath = this.window.getPath();
    const prefix = isReferenceFeedAdvice ? '/reference-feed-advice' : '/feed-advice';

    if (currentPath.includes(`${prefix}/edit/`)) {
      return `${prefix}/view/${response.id}`;
    }

    return prefix;
  }

  selectedCountry$: Observable<string | undefined> = combineLatest([
    this.store.select(UserSelectors.selectCustomer),
    this.store.select(feedAdviceSelectors.getSelectedCountry),
  ]).pipe(
    skipWhile(
      ([customer, selectedCountry]) => customer === undefined && selectedCountry === undefined,
    ),
    map(([customer, selectedCountry]) => {
      const isCustomerProducts = this.window.getPath().includes('/customer-product');
      if (isCustomerProducts) {
        return customer.country;
      } else {
        return selectedCountry;
      }
    }),
  );

  loadData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedAdviceActions.loadData),
      concatMap(() =>
        of(
          feedAdviceActions.clearFeedAdviceState(),
          feedAdviceActions.loadFeedAdvicesRequest(),
          feedAdviceActions.loadSelectedCountry(),
          feedAdviceActions.loadSelectedFeedadviceTypes(),
          feedAdviceActions.loadShowLysineSetting(),
          feedAdviceActions.loadShowEnergySetting(),
          settingActions.loadEnergySetting(),
          settingActions.loadLysineSetting(),
          productActions.getCustomerProductsRequest(),
        ),
      ),
    ),
  );

  loadFeedAdvices$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(feedAdviceActions.loadFeedAdvicesRequest),
      switchMap((_) =>
        combineLatest([
          this.store.select(settingSelectors.getEnergySetting),
          this.store.select(settingSelectors.getLysineSetting),
          this.store.select(UserSelectors.selectCustomer),
        ]).pipe(
          skipWhile(
            ([energy, lysine, customer]) => energy === null || lysine === null || !customer?.id,
          ),
          take(1),
        ),
      ),
      exhaustMap(([energy, lysine]) =>
        this.feedAdviceService.getFeedAdvices(energy, lysine).pipe(
          switchMap((feedAdvices: FeedAdvice[]) =>
            of(
              feedAdviceActions.clearFeedAdviceState(),
              feedAdviceActions.loadFeedAdvicesSuccess({ feedAdvices }),
            ),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              feedAdviceActions.loadFeedAdvicesError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  loadReferenceFeedAdvices$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(feedAdviceActions.loadReferenceFeedAdvicesRequest),
      switchMap((_) =>
        combineLatest([
          this.store.select(settingSelectors.getEnergySetting),
          this.store.select(settingSelectors.getLysineSetting),
          this.store.select(feedAdviceSelectors.getSelectedCountry),
          this.store.select(UserSelectors.selectCustomer),
        ]).pipe(
          skipWhile(
            ([energy, lysine, country, customer]) =>
              energy === null || lysine === null || !country || !customer?.id,
          ),
          take(1),
        ),
      ),
      exhaustMap(([energy, lysine, country]) => {
        if (energy === null || lysine === null || !country) {
          return EMPTY;
        }

        return this.feedAdviceService.getReferenceFeedAdvices(country, energy, lysine).pipe(
          switchMap((referenceFeedAdvices: FeedAdvice[]) =>
            of(
              feedAdviceActions.clearFeedAdviceState(),
              feedAdviceActions.loadFeedAdvicesSuccess({ feedAdvices: referenceFeedAdvices }),
            ),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              feedAdviceActions.loadFeedAdvicesError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        );
      }),
    ),
  );

  upsertFeedAdvice$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(feedAdviceActions.upsertFeedAdviceRequest),
      mergeMap((action) =>
        this.feedAdviceService.upsertFeedAdvice(action.request).pipe(
          switchMap((response: UpsertFeedAdviceResponse) => {
            const savedAdvice = {
              ...action.request,
              id: response.id,
            };

            return of(
              action.isReferenceFeedAdvice
                ? feedAdviceActions.upsertReferenceFeedAdviceSuccess({ response, savedAdvice })
                : feedAdviceActions.upsertFeedAdviceSuccess({ response, savedAdvice }),
              NotificationActions.successSnackbar({
                message: this.localeService.translate(
                  `${action.isReferenceFeedAdvice ? 'reference-' : ''}feed-advice-saved`,
                ),
              }),
            );
          }),
          catchError((error: HttpErrorResponse) =>
            of(
              feedAdviceActions.upsertFeedAdviceError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  upsertFeedAdviceSuccess$: Observable<UpsertFeedAdviceResponse> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(feedAdviceActions.upsertFeedAdviceSuccess),
        map((action) => action.response),
        tap((response) => {
          this.store.dispatch(SiteActions.setNoPendingChanges());
          void this.router.navigateByUrl(this.getPostSaveTarget(response, false));
        }),
      ),
    { dispatch: false },
  );
  upsertReferenceFeedAdviceSuccess$: Observable<UpsertFeedAdviceResponse> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(feedAdviceActions.upsertReferenceFeedAdviceSuccess),
        map((action) => action.response),
        tap((response) => {
          this.store.dispatch(SiteActions.setNoPendingChanges());
          void this.router.navigateByUrl(this.getPostSaveTarget(response, true));
        }),
      ),
    { dispatch: false },
  );

  deleteFeedAdviceWithConfirmation$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(feedAdviceActions.deleteFeedAdviceWithConfirmation),
      switchMap((action) =>
        this.dialogService
          .confirmWarning({
            title: this.localeService.translate(
              `${
                action.isReferenceFeedAdvice ? 'reference-' : ''
              }feed-advice-overview.delete-confirmation-title`,
            ),
            text: this.localeService.translate(
              `${
                action.isReferenceFeedAdvice ? 'reference-' : ''
              }feed-advice-overview.delete-confirmation-message`,
            ),
          })
          .pipe(
            switchMap(
              (result) =>
                (result.status === DIALOG_RETURN_TYPES.confirm && [
                  feedAdviceActions.deleteFeedAdviceRequest({
                    feedAdviceId: action.feedAdviceId,
                    isReferenceFeedAdvice: action.isReferenceFeedAdvice,
                  }),
                ]) ||
                EMPTY,
            ),
          ),
      ),
    ),
  );

  deleteFeedAdviceRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(feedAdviceActions.deleteFeedAdviceRequest),
      mergeMap((action) =>
        this.feedAdviceService.deleteFeedAdvice(action.feedAdviceId).pipe(
          map(() =>
            feedAdviceActions.deleteFeedAdviceSuccess({
              feedAdviceId: action.feedAdviceId,
              isReferenceFeedAdvice: action.isReferenceFeedAdvice,
            }),
          ),
          catchError((_: HttpErrorResponse) =>
            of(
              NotificationActions.errorSnackbar({
                message: this.localeService.translate(
                  `${
                    action.isReferenceFeedAdvice ? 'reference-' : ''
                  }feed-advice-overview.delete-error-message`,
                ),
              }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  deleteFeedAdviceSuccess$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(feedAdviceActions.deleteFeedAdviceSuccess),
        tap((action) =>
          this.router.navigate([`/${action.isReferenceFeedAdvice ? 'reference-' : ''}feed-advice`]),
        ),
      ),
    { dispatch: false },
  );

  loadSelectedCountry$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(feedAdviceActions.loadSelectedCountry),
      exhaustMap((_) =>
        this.settingsService
          .getSingle<string>(
            'NL',
            PigContextKeys.SELECTED_REFERENCEFEEDADVICE_COUNTRY,
            SettingsStorage.Global,
            true,
          )
          .pipe(
            switchMap((settings) =>
              of(feedAdviceActions.setSelectedCountry({ save: false, id: settings })),
            ),
            catchError((error: HttpErrorResponse) =>
              of(NotificationActions.errorSnackbar({ message: error.message })),
            ),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          ),
      ),
    ),
  );

  setSelectedCountry$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(feedAdviceActions.setSelectedCountry),
      switchMap((action) => {
        if (action.save) {
          this.settingsService.setSettings(
            PigContextKeys.SELECTED_REFERENCEFEEDADVICE_COUNTRY,
            action.id,
            SettingsStorage.Global,
            true,
          );
        }
        return of(
          feedAdviceActions.setSelectedCountrySuccess({ id: action.id }),
          productActions.setSelectedCountrySuccess({ id: action.id }),
          productActions.getProductsRequest({ country: action.id }),
        );
      }),
    ),
  );

  /** SHOW_LYSINE_SETTING */
  loadShowLysineSetting$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(feedAdviceActions.loadShowLysineSetting),
      exhaustMap((_) =>
        this.settingsService
          .getSingle<boolean>(
            true,
            PigContextKeys.SHOW_LYSINE_SETTING,
            SettingsStorage.Global,
            true,
            true,
          )
          .pipe(
            switchMap((showLysine: boolean) =>
              of(feedAdviceActions.setShowLysineSetting({ save: false, showLysine })),
            ),
            catchError((error: HttpErrorResponse) =>
              of(NotificationActions.errorSnackbar({ message: error.message })),
            ),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          ),
      ),
    ),
  );

  setShowLysineSetting$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(feedAdviceActions.setShowLysineSetting),
      switchMap((action) => {
        if (action.save) {
          this.settingsService.setSettings(
            PigContextKeys.SHOW_LYSINE_SETTING,
            action.showLysine,
            SettingsStorage.Global,
            true,
            true,
          );
        }
        return of(feedAdviceActions.setShowLysineSettingSuccess({ showLysine: action.showLysine }));
      }),
    ),
  );

  /** SHOW_ENERGY_SETTING */
  loadShowEnergySetting$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(feedAdviceActions.loadShowEnergySetting),
      exhaustMap((_) =>
        this.settingsService
          .getSingle<boolean>(
            true,
            PigContextKeys.SHOW_ENERGY_SETTING,
            SettingsStorage.Global,
            true,
            true,
          )
          .pipe(
            switchMap((showEnergy: boolean) =>
              of(feedAdviceActions.setShowEnergySetting({ save: false, showEnergy })),
            ),
            catchError((error: HttpErrorResponse) =>
              of(NotificationActions.errorSnackbar({ message: error.message })),
            ),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          ),
      ),
    ),
  );

  setShowEnergySetting$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(feedAdviceActions.setShowEnergySetting),
      switchMap((action) => {
        if (action.save) {
          this.settingsService.setSettings(
            PigContextKeys.SHOW_ENERGY_SETTING,
            action.showEnergy,
            SettingsStorage.Global,
            true,
            true,
          );
        }
        return of(feedAdviceActions.setShowEnergySettingSuccess({ showEnergy: action.showEnergy }));
      }),
    ),
  );

  /** SHOW_DOSATOR_SETTING */
  loadShowDosatorSetting$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(feedAdviceActions.loadShowDosatorSetting),
      exhaustMap((_) =>
        this.settingsService
          .getSingle<boolean>(
            true,
            PigContextKeys.SHOW_DOSATOR_SETTING,
            SettingsStorage.Global,
            true,
            true,
          )
          .pipe(
            switchMap((showDosator: boolean) =>
              of(feedAdviceActions.setShowDosatorSetting({ save: false, showDosator })),
            ),
            catchError((error: HttpErrorResponse) =>
              of(NotificationActions.errorSnackbar({ message: error.message })),
            ),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          ),
      ),
    ),
  );

  setShowDosatorSetting$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(feedAdviceActions.setShowDosatorSetting),
      switchMap((action) => {
        if (action.save) {
          this.settingsService.setSettings(
            PigContextKeys.SHOW_DOSATOR_SETTING,
            action.showDosator,
            SettingsStorage.Global,
            true,
            true,
          );
        }
        return of(
          feedAdviceActions.setShowDosatorSettingSuccess({ showDosator: action.showDosator }),
        );
      }),
    ),
  );

  /** SELECTED FEEDADVICE TYPES */
  loadSelectedFeedadviceTypes$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(feedAdviceActions.loadSelectedFeedadviceTypes),
      exhaustMap((_) =>
        this.settingsService
          .getSingle<
            string[]
          >([], PigContextKeys.SELECTED_FEEDADVICE_TYPES, SettingsStorage.Global, true)
          .pipe(
            switchMap((types: string[]) =>
              of(feedAdviceActions.setSelectedFeedadviceTypes({ save: false, types })),
            ),
            catchError((error: HttpErrorResponse) =>
              of(NotificationActions.errorSnackbar({ message: error.message })),
            ),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          ),
      ),
    ),
  );

  setSelectedFeedadviceTypes$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(feedAdviceActions.setSelectedFeedadviceTypes),
      switchMap((action) => {
        if (action.save) {
          this.settingsService.setSettings(
            PigContextKeys.SELECTED_FEEDADVICE_TYPES,
            action.types,
            SettingsStorage.Global,
            true,
          );
        }
        return of(feedAdviceActions.setSelectedFeedadviceTypesSuccess({ types: action.types }));
      }),
    ),
  );

  /* NAVIGATION EFFECTS */
  navigateFeedAdviceDetail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(feedAdviceActions.navigateFeedAdviceDetail),
        map((action) => {
          this.store.dispatch(
            feedAdviceActions.setSelectedFeedAdviceSuccess({ feedAdviceId: action.feedAdviceId }),
          );
          return action;
        }),
        tap((action) =>
          this.router.navigate([
            `${action.isReferenceFeedAdvice ? 'reference-' : ''}feed-advice`,
            action.mode,
            action.feedAdviceId,
          ]),
        ),
      ),
    {
      dispatch: false,
    },
  );
  navigateSowsFeedAdviceDetail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(feedAdviceActions.navigateSowsFeedAdviceDetail),
        tap((action) => {
          this.store.dispatch(
            feedAdviceActions.setSelectedFeedAdviceSuccess({ feedAdviceId: action.feedAdviceId }),
          );
          this.router.navigate([
            `${action.isReferenceFeedAdvice ? 'reference-' : ''}sows-feed-advice`,
            action.mode,
            action.feedAdviceId,
          ]);
        }),
      ),
    {
      dispatch: false,
    },
  );

  navigateFromReferenceRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(feedAdviceActions.navigateFromReferenceRequest),
      withLatestFrom(
        this.store.select(settingSelectors.getEnergySetting),
        this.store.select(settingSelectors.getLysineSetting),
        this.store.select(feedAdviceSelectors.getSelectedCountry),
        this.store.select(UserSelectors.selectCustomer),
      ),
      skipWhile(
        ([_, energy, lysine, country, customer]) =>
          energy === null || lysine === null || !country || customer === undefined,
      ),
      exhaustMap(([action, energy, lysine, country, customer]) => {
        if (energy === null || lysine === null || !country || customer === undefined) {
          return EMPTY;
        }

        return this.feedAdviceService.getReferenceFeedAdvices(country, energy, lysine).pipe(
          switchMap((referenceFeedAdvices: FeedAdvice[]) =>
            of(
              feedAdviceActions.clearFeedAdviceState(),
              productActions.setSelectedCountry({ save: false, id: customer.country }),
              feedAdviceActions.navigateFromReferenceSuccess({
                feedAdvicesFromReference: referenceFeedAdvices.filter(
                  (x) => x.type === action.feedAdviceType,
                ),
                feedAdviceType: action.feedAdviceType,
              }),
            ),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              feedAdviceActions.loadFeedAdvicesError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        );
      }),
    ),
  );

  navigateFromReferenceSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(feedAdviceActions.navigateFromReferenceSuccess),
        tap((_) => this.router.navigate(['/feed-advice', 'from-reference'])),
      ),
    {
      dispatch: false,
    },
  );

  navigateCreateFeedAdvice$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(feedAdviceActions.navigateCreateFeedAdvice),
        tap((action) => {
          switch (action.feedAdviceType) {
            case FeedAdviceType.FATTENING:
            case FeedAdviceType.REARING:
            case FeedAdviceType.WEANED_PIGLETS:
              this.router.navigate([
                `${action.isReferenceFeedAdvice ? 'reference-' : ''}feed-advice`,
                'create',
                action.feedAdviceType,
              ]);
              break;
            case FeedAdviceType.SOWS_BREEDTOFARROW:
            case FeedAdviceType.SOWS_FARROWTOWEAN:
            case FeedAdviceType.SOWS_WEANTOBREED:
              this.router.navigate([
                `${action.isReferenceFeedAdvice ? 'reference-' : ''}sows-feed-advice`,
                'create',
                action.feedAdviceType,
              ]);
              break;
          }
        }),
      ),
    {
      dispatch: false,
    },
  );
}
